// Pages/Settings/EditBuilding.jsx
import React, { useState, useEffect } from 'react';
import { Building, MapPin, CreditCard, Hash, Landmark, UserCheck, Save, X, Edit3 } from 'lucide-react';
import { getBuildingById, updateBuilding } from '../../Fetch/fetchBuilding';
import { getUserBuildings } from '../../Fetch/fetchUser';
import styles from './EditBuilding.module.css';

const EditBuilding = ({ userData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [buildingId, setBuildingId] = useState(null);
    const [buildingData, setBuildingData] = useState({
        address: '',
        city: '',
        addressNumber: '',
        numFloors: '',
        numApartments: '',
        zipCode: '',
        accountNumber: '',
        bankNumber: '',
        bankName: '',
        bankAccountName: '',
        createDate: ''
    });
    const [formData, setFormData] = useState({ ...buildingData });

    useEffect(() => {
        loadBuildingData();
    }, [userData]);

    // Add event listener for building changes
    useEffect(() => {
        const handleBuildingChange = () => {
            if (!isSaving) {
                loadBuildingData();
            }
        };

        window.addEventListener('buildingChanged', handleBuildingChange);

        return () => {
            window.removeEventListener('buildingChanged', handleBuildingChange);
        };
    }, [isSaving]);

    const loadBuildingData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Get user data from localStorage
            const storedUser = localStorage.getItem('user');
            let localUserData = {};
            if (storedUser) {
                localUserData = JSON.parse(storedUser);
            }

            // Get building information
            let buildingInfo = {};
            let currentBuildingId = null;

            try {
                const buildings = await getUserBuildings(localUserData.userId);
                if (buildings && buildings.length > 0) {
                    const currentBuilding = buildings.find(b => 
                        b.buildingId === localUserData.buildingId || 
                        b.buildingID === localUserData.buildingId
                    ) || buildings[0];
                    
                    // Debug: Log all possible floor/apartment field values
                    
                    

                    // Parse combined address
                    const parseAddress = (fullAddress) => {
                        if (!fullAddress) return { street: '', number: '', city: '' };

                        const parts = fullAddress.split(',');
                        if (parts.length === 2) {
                            const streetPart = parts[0].trim();
                            const city = parts[1].trim();

                            const streetWords = streetPart.split(' ');
                            const lastWord = streetWords[streetWords.length - 1];

                            if (/^\d+$/.test(lastWord)) {
                                const street = streetWords.slice(0, -1).join(' ');
                                return { street, number: lastWord, city };
                            } else {
                                return { street: streetPart, number: '', city };
                            }
                        }

                        return { street: fullAddress, number: '', city: '' };
                    };

                    const rawAddress = currentBuilding.address || currentBuilding.Address || '';
                    const parsedAddress = parseAddress(rawAddress);

                    buildingInfo = {
                        address: parsedAddress.street || currentBuilding.street || currentBuilding.Street || 
                                localUserData.selectedBuildingInfo?.address || '',
                        city: parsedAddress.city || currentBuilding.city || currentBuilding.City || 
                             localUserData.selectedBuildingInfo?.city || '',
                        addressNumber: parsedAddress.number || currentBuilding.addressNumber || currentBuilding.AddressNumber || 
                                     localUserData.selectedBuildingInfo?.addressNumber || '',
                        
                        // Enhanced floors mapping with all variations + localStorage fallback
                        numFloors: currentBuilding.numFloors ||
                                  currentBuilding.NumFloors ||
                                  currentBuilding.floors ||
                                  currentBuilding.Floors ||
                                  localUserData.selectedBuildingInfo?.numFloors ||
                                  localUserData.currentBuilding?.numFloors || '',

                        // Enhanced apartments mapping with all variations + localStorage fallback  
                        numApartments: currentBuilding.numApartments ||
                                      currentBuilding.NumApartments ||
                                      currentBuilding.apartments ||
                                      currentBuilding.Apartments ||
                                      localUserData.selectedBuildingInfo?.numApartments ||
                                      localUserData.currentBuilding?.numApartments || '',

                        zipCode: currentBuilding.zipCode || currentBuilding.ZipCode || 
                                localUserData.selectedBuildingInfo?.zipCode || '',
                        accountNumber: '',
                        bankNumber: '',
                        bankName: '',
                        bankAccountName: '',
                        createDate: currentBuilding.createDate || currentBuilding.CreateDate || ''
                    };

                    currentBuildingId = currentBuilding.buildingId || currentBuilding.buildingID || currentBuilding.BuildingID;
                    
                }
            } catch (buildingError) {
                
                // Fallback to localStorage building data
                if (localUserData.selectedBuildingInfo || localUserData.currentBuilding) {
                    const fallbackBuilding = localUserData.selectedBuildingInfo || {};
                    buildingInfo = {
                        address: fallbackBuilding.address || '',
                        city: fallbackBuilding.city || '',
                        addressNumber: fallbackBuilding.addressNumber || '',
                        
                        // Enhanced fallback for floors with multiple sources
                        numFloors: fallbackBuilding.numFloors ||
                                  fallbackBuilding.NumFloors ||
                                  fallbackBuilding.floors ||
                                  fallbackBuilding.Floors ||
                                  localUserData.currentBuilding?.numFloors ||
                                  localUserData.currentBuilding?.NumFloors ||
                                  localUserData.currentBuilding?.floors ||
                                  localUserData.currentBuilding?.Floors || '',

                        // Enhanced fallback for apartments with multiple sources
                        numApartments: fallbackBuilding.numApartments ||
                                      fallbackBuilding.NumApartments ||
                                      fallbackBuilding.apartments ||
                                      fallbackBuilding.Apartments ||
                                      localUserData.currentBuilding?.numApartments ||
                                      localUserData.currentBuilding?.NumApartments ||
                                      localUserData.currentBuilding?.apartments ||
                                      localUserData.currentBuilding?.Apartments || '',

                        zipCode: fallbackBuilding.zipCode || '',
                        accountNumber: fallbackBuilding.accountNumber || '',
                        bankNumber: fallbackBuilding.bankNumber || '',
                        bankName: fallbackBuilding.bankName || '',
                        bankAccountName: fallbackBuilding.bankAccountName || '',
                        createDate: fallbackBuilding.createDate || fallbackBuilding.CreateDate || ''
                    };

                    currentBuildingId = localUserData.buildingId || localUserData.buildingID;
                }
            }

            setBuildingId(currentBuildingId);

            // Fetch complete building data with bank account fields
            if (currentBuildingId) {
                try {
                    const completeBuildingData = await getBuildingById(currentBuildingId);
                    if (completeBuildingData) {
                        buildingInfo.accountNumber = completeBuildingData.accountNumber || completeBuildingData.AccountNumber || '';
                        buildingInfo.bankNumber = completeBuildingData.bankNumber || completeBuildingData.BankNumber || '';
                        buildingInfo.bankName = completeBuildingData.bankName || completeBuildingData.BankName || '';
                        buildingInfo.bankAccountName = completeBuildingData.bankAccountName || completeBuildingData.BankAccountName || '';
                        buildingInfo.createDate = completeBuildingData.createDate || completeBuildingData.CreateDate || '';
                    }
                } catch (buildingDetailsError) {
                }
            }

            setBuildingData(buildingInfo);

            if (!isEditing) {
                setFormData(buildingInfo);
            }

        } catch (error) {
            setError('שגיאה בטעינת פרטי הבניין');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateBankAccountData = (bankData) => {
        const errors = {};

        if (bankData.accountNumber && !/^\d{6,20}$/.test(bankData.accountNumber)) {
            errors.accountNumber = 'מספר חשבון חייב להכיל 6-20 ספרות בלבד';
        }

        if (bankData.bankNumber && (bankData.bankNumber.length !== 3 || !/^\d{3}$/.test(bankData.bankNumber))) {
            errors.bankNumber = 'מספר סניף חייב להיות בדיוק 3 ספרות';
        }

        if (bankData.bankName && bankData.bankName.trim().length === 0) {
            errors.bankName = 'שם הבנק לא יכול להיות ריק';
        }

        if (bankData.bankAccountName && bankData.bankAccountName.trim().length === 0) {
            errors.bankAccountName = 'שם בעל החשבון לא יכול להיות ריק';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);

        try {
            // Validate bank account data
            const bankValidation = validateBankAccountData(formData);
            if (!bankValidation.isValid) {
                const errorMessages = Object.values(bankValidation.errors).join('\n');
                setError('שגיאות בפרטי החשבון:\n' + errorMessages);
                setIsSaving(false);
                return;
            }

            // Validate building data - Remove address validation since fields are disabled
            if (!formData.numFloors || isNaN(formData.numFloors) || formData.numFloors <= 0) {
                setError('מספר קומות חייב להיות מספר חיובי');
                setIsSaving(false);
                return;
            }

            if (!formData.numApartments || isNaN(formData.numApartments) || formData.numApartments <= 0) {
                setError('מספר דירות חייב להיות מספר חיובי');
                setIsSaving(false);
                return;
            }

            if (buildingId) {
                // Get current building data to preserve original values
                const currentBuilding = await getBuildingById(buildingId);

                const buildingUpdateData = {
                    BuildingID: buildingId,
                    CreateDate: formData.createDate || currentBuilding?.CreateDate || new Date().toISOString(),
                    
                    // Preserve original address data since fields are disabled
                    Address: currentBuilding?.Address || buildingData.address,
                    City: currentBuilding?.City || buildingData.city,
                    AddressNumber: currentBuilding?.AddressNumber || buildingData.addressNumber,
                    
                    // Only update editable fields
                    NumFloors: parseInt(formData.numFloors),
                    NumApartments: parseInt(formData.numApartments),
                    ZipCode: formData.zipCode?.trim() || currentBuilding?.ZipCode || '',
                    AccountNumber: formData.accountNumber?.trim() || null,
                    BankNumber: formData.bankNumber?.trim() || null,
                    BankName: formData.bankName?.trim() || null,
                    BankAccountName: formData.bankAccountName?.trim() || null
                };

                await updateBuilding(buildingId, buildingUpdateData);

                // Update local state
                setBuildingData(formData);
                setIsEditing(false);

                // Update localStorage
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    const user = JSON.parse(storedUser);
                    const updatedUser = {
                        ...user,
                        selectedBuildingInfo: {
                            ...user.selectedBuildingInfo,
                            ...formData
                        }
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));

                    // Trigger events to notify other components
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('buildingChanged'));
                    }, 100);
                }

                alert('פרטי הבניין עודכנו בהצלחה!');
            } else {
                setError('לא נמצא מזהה בניין');
            }

        } catch (error) {
            setError(error.message || 'שגיאה בעדכון פרטי הבניין');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData(buildingData);
        setIsEditing(false);
        setError(null);
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <p>טוען פרטי בניין...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    <Building size={24} className={styles.titleIcon} />
                    ערוך פרטי בניין
                </h1>
                <button
                    className={styles.editButton}
                    onClick={() => setIsEditing(!isEditing)}
                    disabled={isSaving}
                >
                    {isEditing ? (
                        <>
                            <X size={16} />
                            ביטול
                        </>
                    ) : (
                        <>
                            <Edit3 size={16} />
                            עריכה
                        </>
                    )}
                </button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}
                </div>
            )}

            <div className={styles.content}>
                {/* Building Information Card */}
                <div className={styles.buildingCard}>
                    <h3 className={styles.cardTitle}>
                        <Building size={20} />
                        מידע על הבניין
                    </h3>
                    <div className={styles.infoGrid}>
                        {/* Address */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <Building size={16} />
                                כתובת
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => handleInputChange('address', e.target.value)}
                                    className={`${styles.infoInput} ${styles.disabledInput}`}
                                    placeholder="הכנס כתובת"
                                    disabled
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.address || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Address Number */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <Hash size={16} />
                                מספר בניין
                            </label>
                            {isEditing ? (
                                <input
                                    type="number"
                                    value={formData.addressNumber}
                                    onChange={(e) => handleInputChange('addressNumber', e.target.value)}
                                    className={`${styles.infoInput} ${styles.disabledInput}`}
                                    placeholder="הכנס מספר בית"
                                    disabled
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.addressNumber || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* City */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <MapPin size={16} />
                                עיר
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    className={`${styles.infoInput} ${styles.disabledInput}`}
                                    placeholder="הכנס עיר"
                                    disabled
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.city || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Zip Code */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <MapPin size={16} />
                                מיקוד
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.zipCode}
                                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                                    className={styles.infoInput}
                                    placeholder="הכנס מיקוד"
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.zipCode || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Create Date */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <Building size={16} />
                                תאריך הקמת בניין
                            </label>
                            {isEditing ? (
                                <input
                                    type="date"
                                    value={formData.createDate ? formData.createDate.split('T')[0] : ''}
                                    onChange={(e) => handleInputChange('createDate', e.target.value)}
                                    className={styles.infoInput}
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.createDate ? new Date(buildingData.createDate).toLocaleDateString('he-IL') : 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Number of Floors */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <Building size={16} />
                                מספר קומות
                            </label>
                            {isEditing ? (
                                <input
                                    type="number"
                                    value={formData.numFloors}
                                    onChange={(e) => handleInputChange('numFloors', e.target.value)}
                                    className={styles.infoInput}
                                    placeholder="הכנס מספר קומות"
                                    min="1"
                                    required
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.numFloors || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Number of Apartments */}
                        <div className={styles.infoItem}>
                            <label className={styles.infoLabel}>
                                <Building size={16} />
                                מספר דירות
                            </label>
                            {isEditing ? (
                                <input
                                    type="number"
                                    value={formData.numApartments}
                                    onChange={(e) => handleInputChange('numApartments', e.target.value)}
                                    className={styles.infoInput}
                                    placeholder="הכנס מספר דירות"
                                    min="1"
                                    required
                                />
                            ) : (
                                <span className={styles.infoValue}>
                                    {buildingData.numApartments || 'לא הוגדר'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Full Address Display */}
                    <div className={styles.addressDisplay}>
                        <h4 className={styles.addressTitle}>כתובת מלאה:</h4>
                        <p className={styles.fullAddress}>
                            {buildingData.address && buildingData.addressNumber && buildingData.city
                                ? `${buildingData.address} ${buildingData.addressNumber}, ${buildingData.city}`
                                : 'לא הוגדר'
                            }
                        </p>
                        <small className={styles.addressNote}>
                            * פרטי הכתובת הם שדות קבועים ולא ניתנים לעריכה
                        </small>
                    </div>
                </div>

                {/* Bank Account Information Card */}
                <div className={styles.bankCard}>
                    <h3 className={styles.cardTitle}>
                        <CreditCard size={20} />
                        פרטי חשבון בנק להעברות
                    </h3>
                    <div className={styles.bankInfo}>
                        {/* Account Number */}
                        <div className={styles.bankRow}>
                            <span className={styles.bankKey}>
                                <Hash size={16} />
                                מספר חשבון:
                            </span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.accountNumber || ''}
                                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                                    className={styles.bankInput}
                                    placeholder="הכנס מספר חשבון (6-20 ספרות)"
                                    maxLength="20"
                                />
                            ) : (
                                <span className={styles.bankValue}>
                                    {buildingData.accountNumber || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Bank Number */}
                        <div className={styles.bankRow}>
                            <span className={styles.bankKey}>
                                <Building size={16} />
                                מספר סניף:
                            </span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.bankNumber || ''}
                                    onChange={(e) => handleInputChange('bankNumber', e.target.value)}
                                    className={styles.bankInput}
                                    placeholder="הכנס מספר סניף (3 ספרות)"
                                    maxLength="3"
                                />
                            ) : (
                                <span className={styles.bankValue}>
                                    {buildingData.bankNumber || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Bank Name */}
                        <div className={styles.bankRow}>
                            <span className={styles.bankKey}>
                                <Landmark size={16} />
                                שם הבנק:
                            </span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.bankName || ''}
                                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                                    className={styles.bankInput}
                                    placeholder="הכנס שם הבנק (למשל: בנק הפועלים)"
                                    maxLength="100"
                                />
                            ) : (
                                <span className={styles.bankValue}>
                                    {buildingData.bankName || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {/* Bank Account Name */}
                        <div className={styles.bankRow}>
                            <span className={styles.bankKey}>
                                <UserCheck size={16} />
                                שם בעל החשבון:
                            </span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.bankAccountName || ''}
                                    onChange={(e) => handleInputChange('bankAccountName', e.target.value)}
                                    className={styles.bankInput}
                                    placeholder="הכנס שם בעל החשבון כפי שרשום בבנק"
                                    maxLength="100"
                                />
                            ) : (
                                <span className={styles.bankValue}>
                                    {buildingData.bankAccountName || 'לא הוגדר'}
                                </span>
                            )}
                        </div>

                        {!isEditing &&
                            !buildingData.accountNumber &&
                            !buildingData.bankNumber &&
                            !buildingData.bankName &&
                            !buildingData.bankAccountName && (
                                <div className={styles.noDataMessage}>
                                    <CreditCard size={24} />
                                    <p>טרם הוגדרו פרטי חשבון בנק עבור הבניין</p>
                                    <small>פרטים אלה נדרשים לקבלת תשלומים מהדיירים</small>
                                </div>
                            )}
                    </div>
                </div>

                {/* Save/Cancel Actions */}
                {isEditing && (
                    <div className={styles.bottomActions}>
                        <button
                            className={styles.saveButton}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            <Save size={16} />
                            {isSaving ? 'שומר...' : 'שמור שינויים'}
                        </button>
                        <button
                            className={styles.cancelButton}
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            <X size={16} />
                            ביטול
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditBuilding;